// util/API.js
import axios from 'axios';

import {
  clearAuthSessionStorage,
  persistAuthSession,
  readAuthSession,
} from './authSessionStorage';

let onUnauthorized = null;
let onSessionTokensUpdated = null;
let isHandlingUnauthorized = false;
let inFlightRefreshPromise = null;

function readString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeAuthPayload(raw) {
  const token = readString(raw?.token);
  const refreshToken = readString(raw?.refreshToken);
  const sessionId = readString(raw?.sessionId);
  const sessionExpiresAt = readString(raw?.sessionExpiresAt);

  return {
    refreshToken,
    sessionExpiresAt,
    sessionId,
    token,
  };
}

function emitSessionTokenUpdate(payload) {
  if (!onSessionTokensUpdated) return;
  try {
    onSessionTokensUpdated(payload);
  } catch {
    // no-op
  }
}

function handleUnauthorized() {
  if (isHandlingUnauthorized) return;

  isHandlingUnauthorized = true;
  try {
    clearAuthSessionStorage();
    // Keep cart, payment-session and voucher state: a token refresh failure is
    // not a user action, and wiping payment state orphaned gateway returns.
    try {
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('cherrfy_session_expired', '1');
      }
    } catch {
      // ignore storage failures
    }

    if (onUnauthorized) {
      onUnauthorized();
      return;
    }

    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  } finally {
    isHandlingUnauthorized = false;
  }
}

export const setUnauthorizedHandler = (handler) => {
  onUnauthorized = handler;
};

export const setSessionTokenUpdateHandler = (handler) => {
  onSessionTokensUpdated = handler;
};

// API base URL resolution order:
// 1) REACT_APP_API_BASE_URL — CRA auto-loads .env.development (npm start)
//    or .env.production (npm run build), so no manual comment toggling is
//    needed before switching environments.
// 2) The hostname fallback below, used only when no env file is present.
const resolveApiBaseUrl = () => {
  const envBase = String(
    process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || ""
  ).trim();
  if (envBase) return envBase;

  if (typeof window !== "undefined") {
    const host = String(window.location?.hostname || "").toLowerCase();
    if (host === "localhost" || host === "127.0.0.1") {
      return "http://localhost:8080/api";
    }
  }

  return "https://api.cherrfy.com/api";
};

const API = axios.create({
  baseURL: resolveApiBaseUrl(),
  timeout: 20000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

async function refreshSessionTokens() {
  if (inFlightRefreshPromise) return inFlightRefreshPromise;

  inFlightRefreshPromise = (async () => {
    const session = readAuthSession();
    const refreshToken = readString(session.refreshToken);

    if (!refreshToken) {
      throw new Error('No refresh token found for session refresh.');
    }

    try {
      const response = await API.post(
        '/user/refresh-token',
        {
          refreshToken,
          sessionId: readString(session.sessionId),
        },
        {
          headers: { Authorization: '' },
          skipAuthRefresh: true,
        },
      );

      const payload = normalizeAuthPayload(response?.data?.data || {});
      if (!payload.token) {
        throw new Error('Refresh response did not include an access token.');
      }

      const nextSession = {
        refreshToken: payload.refreshToken || refreshToken,
        sessionExpiresAt: payload.sessionExpiresAt || readString(session.sessionExpiresAt),
        sessionId: payload.sessionId || readString(session.sessionId),
        token: payload.token,
      };

      persistAuthSession(nextSession);
      emitSessionTokenUpdate(nextSession);
      return nextSession;
    } catch (error) {
      // Refresh race fallback: another tab/request may have already updated localStorage.
      if (error?.response?.status === 409) {
        const latest = readAuthSession();
        if (latest.token && latest.token !== session.token) {
          return latest;
        }
      }
      throw error;
    }
  })().finally(() => {
    inFlightRefreshPromise = null;
  });

  return inFlightRefreshPromise;
}

API.interceptors.request.use((config) => {
  const hasExplicitAuthHeader =
    Boolean(config?.headers) &&
    Object.prototype.hasOwnProperty.call(config.headers, 'Authorization');
  if (hasExplicitAuthHeader) {
    return config;
  }

  const { token } = readAuthSession();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config || {};
    const hadAuthHeader = Boolean(originalRequest?.headers?.Authorization);
    const isRefreshRequest = String(originalRequest?.url || '').includes('/user/refresh-token');
    const skipAuthRefresh = Boolean(originalRequest?.skipAuthRefresh);
    const hasSessionToken = Boolean(readAuthSession().token);

    const canAttemptRefresh =
      (status === 401 || status === 419) &&
      hadAuthHeader &&
      !isRefreshRequest &&
      !skipAuthRefresh &&
      !originalRequest._retry &&
      hasSessionToken;

    if (canAttemptRefresh) {
      originalRequest._retry = true;

      try {
        const refreshed = await refreshSessionTokens();
        const nextToken = readString(refreshed?.token);

        if (!nextToken) {
          throw new Error('Session refresh did not provide access token.');
        }

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${nextToken}`;
        return API(originalRequest);
      } catch {
        handleUnauthorized();
      }
    }

    if ((status === 401 || status === 419) && hadAuthHeader && hasSessionToken && (skipAuthRefresh || isRefreshRequest)) {
      handleUnauthorized();
    }

    return Promise.reject(error);
  },
);

export default API;
